import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, Renderer2, ViewChildren } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MenuItemEvent } from '@shared/menuItemEvent';
import { IReport } from '@shared/services/report.service';
import { UserPermissionsService } from '@shared/services/userpermissions.service';
import { SidebarItem } from '@shared/sidebaritem';
import { Subject, takeUntil } from 'rxjs';
import { hasTopLevelPermission } from '../auth/top-level-permission.guard';
import { ListItemComponent } from './list-item/list-item.component';

export interface IData {
	report: IReport;
}

@Component({
	selector: 'app-menu-list-item',
	templateUrl: './menu-list-item.component.html',
	styleUrls: ['./menu-list-item.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class MenuListItemComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input() expanded: boolean;
	@Input() item: SidebarItem;
	@Input() depth: number;
	@Input() isExpanded: boolean;
	@Input() overlay: boolean;
	isOverlayOpen: boolean;
	@Output() newElementEvent = new EventEmitter<MenuItemEvent>();
	selected = '';
	@ViewChildren(ListItemComponent) items: QueryList<ListItemComponent>;
	public filter = new UntypedFormControl('');

	private destroyNotifier$ = new Subject<void>();

	public readonly overlayOptions: Partial<CdkConnectedOverlay> = {
		hasBackdrop: true,
		positions: [
			{
				originX: 'end',
				originY: 'top',
				overlayX: 'start',
				overlayY: 'top',
			},
		],
		/* You can add to this object all of these options */
		// backdropClass: '',
		// flexibleDimensions: false,
		// growAfterOpen: false,
		// height: 'auto',
		// width: 'auto',
		// lockPosition: true,
		// minHeight: 'unset',
		// minWidth: 'unset',
		// offsetX: 0,
		// offsetY: 0,
		// panelClass: '',
		// positionStrategy,
		// push,
		// scrollStrategy,
		// transformOriginSelector,
		// viewportMargin,
	};

	private keyManager: ActiveDescendantKeyManager<ListItemComponent>;

	constructor(
		public router: Router,
		public dialog: MatDialog,
		private renderer: Renderer2,
		private userPermissionsService: UserPermissionsService,
	) {
		if (this.depth === undefined) {
			this.depth = 0;
		}
	}

	ngAfterViewInit(): void {
		this.keyManager = new ActiveDescendantKeyManager(this.items).withWrap().withTypeAhead();
	}

	ngOnDestroy(): void {
		this.destroyNotifier$.next();
		this.destroyNotifier$.complete();
	}

	ngOnInit(): void {
		this.renderer.listen(document, `keydown.${this.item.shortcut}`, (event) => {
			event.preventDefault();
			const element = document.getElementById(this.item.icon);
			element?.click();
		});

		this.userPermissionsService.permissions$.pipe(takeUntil(this.destroyNotifier$)).subscribe();
	}

	onItemSelected(event: any, item: SidebarItem) {
		const menuItemEvent = new MenuItemEvent(event.currentTarget, item);
		if (!item.children || !item.children.length) {
			if (item.path.includes('mes-production')) location.href = '/mes-production/terminal';
			else void this.router.navigate([item.path]);
		}
		if (item.children && item.children.length) {
			if (this.isExpanded) {
				// Refers to the expanded state of the whole left menu (is it folded into "icons only" or not)
				this.expanded = !this.expanded;
			} else {
				this.expanded = true; // We want to show and fold out the parent's children if the click happens from "icons-only" state
			}
		}
		this.newElementEvent.emit(menuItemEvent);
	}

	hasAccess(permission: string | string[]) {
		const required = (Array.isArray(permission) ? permission : [permission]).filter((p) => p !== '');
		if (required.length === 0) {
			return true; // No permission required
		}
		if (this.item?.topLevelPermission) {
			return required.every(hasTopLevelPermission);
		}
		return this.userPermissionsService.hasPermissions(required);
	}
}
